import axios, { AxiosError, AxiosRequestConfig, RawAxiosRequestHeaders, AxiosProgressEvent } from 'axios'

import { AuthStore } from '../auth'

import { Store } from '../general'
import { I18nStore } from '../i18n'

import ApiError from './error'

interface RootStore {
  auth: AuthStore
  i18n: I18nStore
}

export interface ApiConfig {
  baseUrl?: string
  onError?: (message: Error) => void
}

interface ErrorResponse {
  errors?: Record<string, string[]>
  message?: string
}

export interface ApiRequestConfig extends Omit<AxiosRequestConfig, 'method' | 'url' | 'data' | 'headers'> {
  token?: string
  headers?: RawAxiosRequestHeaders
}

interface RequestConfig extends Omit<AxiosRequestConfig, 'headers'> {
  headers?: RawAxiosRequestHeaders
}

class Api extends Store<RootStore> {
  constructor(
    store: RootStore,
    private config: ApiConfig = {},
  ) {
    super(store)
    console.debug(`API: Initialized with url ${this.config.baseUrl}`)
  }

  async get<T>(path: string, config?: ApiRequestConfig) {
    const { token, ...otherConfig } = config || {}
    const axiosConfig: RequestConfig = {
      method: 'GET',
      ...otherConfig,
      headers: {
        ...otherConfig.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
    return this.request<T>(path, axiosConfig)
  }

  async post<T>(path: string, data?: any, config?: ApiRequestConfig) {
    const { token, ...otherConfig } = config || {}
    const axiosConfig: RequestConfig = {
      method: 'POST',
      data,
      ...otherConfig,
      headers: {
        ...otherConfig.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }

    return this.request<T>(path, axiosConfig)
  }

  async put<T>(path: string, data?: any, config?: ApiRequestConfig) {
    const { token, ...otherConfig } = config || {}
    const axiosConfig: RequestConfig = {
      method: 'PUT',
      data,
      ...otherConfig,
      headers: {
        ...otherConfig.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
    return this.request<T>(path, axiosConfig)
  }

  async patch<T>(path: string, data?: any, config?: ApiRequestConfig) {
    const { token, ...otherConfig } = config || {}
    return this.request<T>(path, {
      method: 'PATCH',
      data,
      ...otherConfig,
      headers: {
        ...otherConfig.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
  }

  async delete<T>(path: string, config?: ApiRequestConfig) {
    const { token, ...otherConfig } = config || {}
    const axiosConfig: RequestConfig = {
      method: 'DELETE',
      ...otherConfig,
      headers: {
        ...otherConfig.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
    return this.request<T>(path, axiosConfig)
  }

  async upload<T>(path: string, formData: FormData, callback?: (progress: number) => void, config?: ApiRequestConfig) {
    const { token, ...otherConfig } = config || {}

    const axiosConfig: RequestConfig = {
      method: 'POST',
      ...otherConfig,
      headers: {
        ...otherConfig.headers,
        'Content-Type': 'multipart/form-data',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      data: formData,
      ...(callback
        ? {
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              const { loaded, total } = progressEvent
              const progress = total ? loaded / total : 0
              callback?.(progress)
            },
          }
        : {}),
    }

    return this.request<T>(path, axiosConfig)
  }

  private async request<T>(path: string, config: RequestConfig): Promise<T> {
    const url = path?.startsWith('http') ? path : `${this.config.baseUrl}/${path}`
    console.debug(`API: Request [${config?.method} ${path}] ${config.data ? JSON.stringify(config.data) : ''}`)
    const { auth } = this.store
    const headers = {
      'Accept-Language': this.store.i18n.language,
      ...(!config.headers?.Authorization && auth?.token && !path?.startsWith('http')
        ? { Authorization: `Bearer ${auth.token}` }
        : {}),
      ...config.headers,
    }
    const requestConfig: RequestConfig = {
      ...config,
      url,
      headers,
    }

    try {
      const response = await axios.request<T>(requestConfig)
      console.debug(`API: Response [${config?.method} ${path}] ${JSON.stringify(response?.data)}`)
      return response?.data
    } catch (error) {
      const { response, status } = error as AxiosError<ErrorResponse>
      const fields =
        response?.data.errors && typeof response?.data.errors === 'object'
          ? Object.keys(response?.data.errors).reduce(
              (acc, field) => ({
                ...acc,
                [field]: response.data.errors![field]?.join(', ') || '',
              }),
              {} as Record<string, string>,
            )
          : undefined
      const message = response?.data.message || (error as Error).message

      const log = `[${config?.method} ${path}] ${status} ${message}${
        fields
          ? ` [${Object.keys(fields)
              .map?.((field) => `${field}: ${fields[field]}`)
              .join(', ')}]`
          : ''
      }`
      console.debug(`API: Error ${log}`)
      const apiError = new ApiError(message, {
        fields,
        status,
      })
      this.config.onError?.(new Error(log))
      throw apiError
    }
  }
}

export default Api
